import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthTextField } from "./TextField";
import QRCode from "react-qr-code";
import { SERVER_ENV } from "../../config";

const mfaSetupSchema = z.object({
    code: z.string().min(1, "Verification code is required")
});

type MfaSetupFormData = z.infer<typeof mfaSetupSchema>;

interface MfaSetupFormProps {
    totp: string;
    email: string;
    onSubmit: (data: { code: string }) => Promise<void>;
    onBack: () => void;
    isLoading: boolean;
}

export default function MfaSetupForm({ totp, email, onSubmit, onBack, isLoading }: MfaSetupFormProps) {
    const methods = useForm<MfaSetupFormData>({
        resolver: zodResolver(mfaSetupSchema),
        mode: "onChange"
    });

    const handleSubmit = methods.handleSubmit(async (data) => {
        await onSubmit(data);
    });

    const issuer = `BSSI-PP-AdminConsole-${SERVER_ENV}`;
    const qrCodeValue = `otpauth://totp/${issuer}:${email}?secret=${totp}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;

    return (
        <FormProvider {...methods}>
            <Box component="form" onSubmit={handleSubmit}>
                <Typography
                    variant="h4"
                    textAlign="center"
                    gutterBottom
                    color="primary"
                    sx={{ fontWeight: 500, mb: 3 }}
                >
                    Set Up Two-Factor Authentication
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                    Please scan this QR code with your authenticator app:
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 4,
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: 1
                    }}
                >
                    <QRCode
                        size={256}
                        style={{ height: "auto", maxWidth: "100%", width: "100%", maxHeight: "200px" }}
                        value={qrCodeValue}
                        viewBox="0 0 256 256"
                    />
                </Box>

                <Typography variant="body2" sx={{ mb: 2 }}>
                    Enter the verification code from your authenticator app:
                </Typography>

                <AuthTextField
                    name="code"
                    label="Verification Code"
                    placeholder="Enter 6-digit code"
                    fullWidth
                    margin="normal"
                    data-cy="2fa-input"
                />

                <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    sx={{ mt: 3, mb: 2 }}
                    disabled={!methods.formState.isValid || isLoading}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Verify and Complete Setup"}
                </Button>

                <Button variant="text" onClick={onBack} fullWidth sx={{ color: "text.secondary" }}>
                    Back to Login
                </Button>
            </Box>
        </FormProvider>
    );
}
